import { nowIso, type ConsentVersionRow, type HandbookVersionRow } from '@routeback/core';
import { useToast } from '@routeback/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignaturePad } from '../../components/SignaturePad';
import { useApp } from '../../lib/app';
import { useFileUrl } from '../../lib/fileCache';
import { addFile } from '../../lib/photos';
import { formatRange, todayIn } from '../timeoff/timeoff';
import { defaultAckText } from './signoff';
import { useSignoffs } from './useSignoffs';
import { FileOpenLink } from '../../components/FileOpenLink';

/**
 * Sign the company's handbook and the camera/biometric consent — the Driver
 * Portal's two steps. Drivers land here until both are signed; managers
 * come here from the reminder. Works offline (the signature saves on the
 * phone and is sent when there's signal; the server stamps the time and IP).
 */
export function SignOffView() {
  const app = useApp();
  const nav = useNavigate();
  const s = useSignoffs();
  const total = useRef<number | null>(null);
  if (!s) return <div className="spinner" />;
  const { handbook, consents } = s.pending;
  const steps = (handbook ? 1 : 0) + consents.length;
  if (total.current === null && steps) total.current = steps;
  const of = Math.max(total.current ?? steps, steps);
  const step = of - steps + 1;
  if (!steps) return (
    <div className="card" style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>✅</div>
      <h3 style={{ margin: '6px 0' }}>You're all signed</h3>
      <p className="muted">Thanks — you're up to date with {app.details.name || 'your company'}'s handbook and consents.</p>
      <button className="btn btn-primary" onClick={() => nav('/home', { replace: true })}>Continue</button>
    </div>
  );
  return (
    <div>
      {s.blocked && (
        <div className="card" style={{ borderColor: 'rgba(245,166,35,.5)' }}>
          <strong>Before you start</strong>
          <p className="small" style={{ margin: '4px 0 0' }}>{app.details.name || 'Your company'} needs your signature on {of === 1 ? 'one document' : `${of} documents`}. It takes a couple of minutes.</p>
          <p className="muted small" style={{ margin: '6px 0 0' }}>In an accident? <Link to="/accident">Go to the accident report</Link> — it's always open.</p>
        </div>
      )}
      {handbook ? <HandbookStep key={handbook.id} v={handbook} step={step} of={of} /> : <ConsentStep key={consents[0]!.id} v={consents[0]!} step={step} of={of} />}
    </div>
  );
}

function SignBlock({ onSign, busy, label }: { onSign: (name: string, sig: Blob) => void; busy: boolean; label: string }) {
  const app = useApp();
  const [name, setName] = useState(app.userName === 'You' ? '' : app.userName);
  const [sig, setSig] = useState<Blob | null>(null);
  const today = todayIn(app.details.timezone);
  return (
    <>
      <div className="ar-pair">
        <div className="field"><label htmlFor="so-name">Your full name</label><input id="so-name" value={name} autoComplete="name" onChange={(e) => setName(e.target.value)} /></div>
        <div className="field"><span className="ar-label">Date</span><div style={{ padding: '10px 0' }}>{formatRange(today, today)}</div></div>
      </div>
      <SignaturePad onChange={setSig} />
      <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} disabled={!name.trim() || !sig || busy || !app.active} onClick={() => sig && onSign(name.trim(), sig)}>{busy ? 'Saving…' : label}</button>
      {(!name.trim() || !sig) && <p className="muted small" style={{ textAlign: 'center' }}>Type your name and sign in the box.</p>}
    </>
  );
}

function HandbookStep({ v, step, of }: { v: HandbookVersionRow; step: number; of: number }) {
  const app = useApp();
  const toast = useToast();
  const file = useFileUrl('company-assets', v.file_path || null);
  const href = v.link_url || file.url || '';
  const [opened, setOpened] = useState(!href);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const sign = async (name: string, sig: Blob) => {
    setBusy(true);
    const path = await addFile(sig, app.company.id, app.userId, 'handbook', 'signatures', 'png');
    await app.repos.handbookAcks.save(app.repos.handbookAcks.blank({
      user_id: app.userId, handbook_version_id: v.id, typed_name: name, signature_path: path, ip: '', user_agent: navigator.userAgent.slice(0, 400), signed_at: nowIso(),
    }));
    setBusy(false);
    toast('Handbook signed');
  };
  return (
    <>
      <div className="card">
        <div className="muted small">Step {step} of {of}</div>
        <h3 style={{ margin: '2px 0 10px' }}>{v.title || 'Employee handbook'} <span className="muted small">({v.version})</span></h3>
        <div className="op2-step"><span>1</span><strong>Open the handbook</strong></div>
        {href ? (
          <FileOpenLink className="btn btn-block" url={href} title={v.title || 'Employee handbook'} onOpen={() => setOpened(true)}>📘 Open the handbook</FileOpenLink>
        ) : <p className="muted small">Your company hasn't attached the handbook file — ask your manager for a copy.</p>}
      </div>
      <div className="card">
        <div className="op2-step"><span>2</span><strong>Acknowledge</strong></div>
        <label className={'row so-check' + (opened ? '' : ' so-locked')} style={{ gap: 10, alignItems: 'flex-start' }}>
          <input type="checkbox" checked={agree} disabled={!opened} onChange={(e) => setAgree(e.target.checked)} style={{ width: 22, height: 22, marginTop: 2, flex: 'none' }} />
          <span>{v.ack_text || defaultAckText(app.details.name)}</span>
        </label>
        {!opened && <p className="muted small">Open the handbook first.</p>}
      </div>
      {agree && (
        <div className="card">
          <div className="op2-step"><span>3</span><strong>Sign</strong></div>
          <SignBlock onSign={(n, s) => void sign(n, s)} busy={busy} label={step < of ? 'Sign and go to the next step' : 'Sign and continue'} />
        </div>
      )}
    </>
  );
}

function ConsentStep({ v, step, of }: { v: ConsentVersionRow; step: number; of: number }) {
  const app = useApp();
  const toast = useToast();
  const box = useRef<HTMLDivElement>(null);
  const [read, setRead] = useState(false);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  // Reading to the end unlocks the checkbox (short texts unlock right away).
  useEffect(() => {
    const el = box.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setRead(true);
  }, [v.body_text]);
  const onScroll = () => {
    const el = box.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setRead(true);
  };
  const sign = async (name: string, sig: Blob) => {
    setBusy(true);
    const path = await addFile(sig, app.company.id, app.userId, 'consent', 'signatures', 'png');
    await app.repos.consentSignatures.save(app.repos.consentSignatures.blank({
      user_id: app.userId, consent_version_id: v.id, typed_name: name, signature_path: path, ip: '', user_agent: navigator.userAgent.slice(0, 400), signed_at: nowIso(),
    }));
    setBusy(false);
    toast('Consent signed');
  };
  return (
    <>
      <div className="card">
        <div className="muted small">Step {step} of {of}</div>
        <h3 style={{ margin: '2px 0 10px' }}>{v.title || 'Camera & biometric consent'} <span className="muted small">({v.version})</span></h3>
        <div className="op2-step"><span>1</span><strong>Read the consent</strong></div>
        <div ref={box} className="so-text" onScroll={onScroll} tabIndex={0} aria-label="Consent text">{v.body_text || 'Your company hasn’t added the consent wording yet.'}</div>
        {!read && <p className="muted small">Scroll to the end to continue.</p>}
        {v.policy_url && <a className="btn btn-sm btn-ghost" style={{ marginTop: 8 }} href={v.policy_url} target="_blank" rel="noopener noreferrer">Read the full privacy policy</a>}
        <label className={'row so-check' + (read ? '' : ' so-locked')} style={{ gap: 10, alignItems: 'flex-start', marginTop: 12 }}>
          <input type="checkbox" checked={agree} disabled={!read} onChange={(e) => setAgree(e.target.checked)} style={{ width: 22, height: 22, marginTop: 2, flex: 'none' }} />
          <span>I have read this Notice and Consent and I agree to it.</span>
        </label>
      </div>
      {agree && (
        <div className="card">
          <div className="op2-step"><span>2</span><strong>Sign</strong></div>
          <SignBlock onSign={(n, s) => void sign(n, s)} busy={busy} label={step < of ? 'Sign and go to the next step' : 'Sign and continue'} />
        </div>
      )}
    </>
  );
}
